// Frontend/src/hooks/useResidents.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listResidents,
  getResident,
  createResident,
  updateResident,
  deleteResident,
  importResidentsCSV,
  downloadResidentCSVTemplate,
  type ResidentList,
  type Resident,
  type CreateResidentData,
  type UpdateResidentData,
  type ResidentCSVImportResult,
} from "@/api/residents";

/**
 * Query key factory for residents
 */
export const residentKeys = {
  all: ["residents"] as const,
  lists: () => [...residentKeys.all, "list"] as const,
  list: () => [...residentKeys.lists()] as const,
  details: () => [...residentKeys.all, "detail"] as const,
  detail: (id: string) => [...residentKeys.details(), id] as const,
};

/**
 * Fetch all residents for the current condo
 */
export function useResidents() {
  return useQuery<ResidentList[], Error>({
    queryKey: residentKeys.list(),
    queryFn: listResidents,
  });
}

/**
 * Fetch a single resident by ID
 */
export function useResident(id: string) {
  return useQuery<Resident, Error>({
    queryKey: residentKeys.detail(id),
    queryFn: () => getResident(id),
    enabled: !!id,
  });
}

/**
 * Create a new resident
 */
export function useCreateResident() {
  const queryClient = useQueryClient();

  return useMutation<Resident, Error, CreateResidentData>({
    mutationFn: createResident,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: residentKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: ["buildings"] }),
        queryClient.invalidateQueries({ queryKey: ["units"] }),
      ]);
    },
  });
}

/**
 * Update an existing resident
 */
export function useUpdateResident() {
  const queryClient = useQueryClient();

  return useMutation<Resident, Error, { id: string; data: UpdateResidentData }>({
    mutationFn: ({ id, data }) => updateResident(id, data),
    onSuccess: async (data) => {
      queryClient.setQueryData(residentKeys.detail(data.id), data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: residentKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: ["buildings"] }),
        queryClient.invalidateQueries({ queryKey: ["units"] }),
      ]);
    },
  });
}

/**
 * Delete a resident
 */
export function useDeleteResident() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteResident,
    onSuccess: async (_, deletedId) => {
      queryClient.removeQueries({ queryKey: residentKeys.detail(deletedId) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: residentKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: ["buildings"] }),
        queryClient.invalidateQueries({ queryKey: ["units"] }),
      ]);
    },
  });
}

/**
 * Import residents from CSV
 */
export function useImportResidentsCSV() {
  const queryClient = useQueryClient();

  return useMutation<ResidentCSVImportResult, Error, File>({
    mutationFn: importResidentsCSV,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: residentKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: ["buildings"] }),
        queryClient.invalidateQueries({ queryKey: ["units"] }),
      ]);
    },
  });
}

/**
 * Download CSV template
 */
export function useDownloadResidentCSVTemplate() {
  return useMutation<string, Error>({
    mutationFn: downloadResidentCSVTemplate,
  });
}
